<template>
    <div
        class="search-product"
        :style="{ borderBottom: solo ? '1px solid #c7cbd6' : ''}"
    >
        <v-text-field
            ref="searchField"
            class="pa-0 ma-0"
            v-model="search"
            :disabled="disabled"
            :label="label"
            :height="height"
            :error-messages="errorMessages"
            :hide-details="hideDetails"
            clear-icon="fal fa-times"
            clearable
            :solo="solo"
            :flat="solo"
            :dense="dense"
            :prefix="quantity ? `${quantity} x ` : ''"
            @blur="onSearchBlur"
            @focus="search = null"
            @click:clear="clear"
        />

        <div
            v-if="showDropdownMenu"
            ref="dropdownMenu"
            v-click-outside="menuClickOutside"
            class="select-products-menu"
            :style="{ top: height }"
        >
            <v-list class="pa-0">
                <v-list-item
                    v-for="(prod, index) in filteredProducts"
                    :key="index"
                    class="search-item"
                    :class="{ active: index === activeProductIndex }"
                    selectable
                    @click="selectProduct(prod)"
                >
                    <v-list-item-content>
                        <v-list-item-title>{{ prod.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                            <span v-if="prod.code" class="mr-2">
                                {{ $t('general.code') }}: {{ prod.code }}
                            </span>
                            <span v-if="prod.barcode" class="mr-2">
                                {{ $t('general.barcode') }}: {{ prod.barcode }}
                            </span>
                            <span
                                v-if="!isNaN(prod.count) && !tenant.venue.settings.hide_product_quantities && tenant.venue.settings.show_count_in_product_search"
                                :class="{ 'error--text': prod.count < 0 }"
                            >
                                {{ $t('general.quantity') }}: {{ prod.getCount() }}
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="prod.selling_price">
                        <div>{{ prod.getPriceAfterTax().toFormat() }}</div>
                        <small class="price-pre-tax">
                            ({{ $t('general.pre_tax') }} {{ prod.getPriceBeforeTax().toFormat() }})
                        </small>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </div>

        <v-btn
            v-if="device.platform === 'android'"
            class="mt-1 mr-3"
            large
            icon
            :disabled="disabled"
            @click="scanBarcode"
        >
            <v-icon size="26">fal fa-barcode-read</v-icon>
        </v-btn>

        <slot name="append" />
    </div>
</template>

<script>
import Product from '@/models/Product';
import { AriaCapacitor } from '@codeus/aria-capacitor';

export default {
    name: 'SearchProduct',

    props: {
        disableKeys: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        products: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: function() {
                return this.$t('general.search');
            },
        },
        height: {
            type: String,
            default: '32px',
        },
        disableQuantity: {
            type: Boolean,
            default: false,
        },
        preserveSelection: {
            type: Boolean,
            default: true,
        },
        solo: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: Boolean,
            default: false,
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
    },

    created() {
        document.addEventListener('keyup', this.backgroundKeyUpHandler);
    },

    beforeDestroy() {
        document.removeEventListener('keyup', this.backgroundKeyUpHandler);
    },

    data() {
        return {
            search: null,
            quantity: null,
            filteredProducts: [],
            product: null,

            showDropdownMenu: false,
            activeProductIndex: null,
        }
    },

    watch: {
        search(val) {
            if(this.product && this.product.name === val) return;

            this.debounceSearch()
        },
    },

    methods: {
        backgroundKeyUpHandler(event) {
            if(this.disableKeys) return

            if(event.code === 'Backspace' && !this.search) {
                this.quantity = null
                return
            }

            // handle up / down arrow keys when dropdown menu is opened
            if (event.keyCode === 38 && this.showDropdownMenu && this.activeProductIndex > 0) {
                this.activeProductIndex--
                this.$refs.dropdownMenu.scrollTop = this.activeProductIndex > 4 ? (this.activeProductIndex - 4) * 67 : 0
                return
            } else if (event.keyCode === 40 && this.showDropdownMenu && this.activeProductIndex < this.filteredProducts.length - 1) {
                this.activeProductIndex++
                this.$refs.dropdownMenu.scrollTop = this.activeProductIndex > 4 ? (this.activeProductIndex - 4) * 67 : 0
                return
            }

            if(event.keyCode === 13) {
                if(!this.search) return

                this.filterProducts()

                if(!this.showDropdownMenu) {
                    this.activeProductIndex = this.filteredProducts.length > 0 ? 0 : null
                }

                const activeProduct = this.activeProductIndex !== null ? this.filteredProducts[this.activeProductIndex] : null
                if(activeProduct) {
                    this.selectProduct(activeProduct)
                    return
                }

                this.resetSearch()

                this.$emit('not-found');
            }
        },

        debounceSearch: _.debounce(function() {
            this.filterProducts()
            this.activeProductIndex = this.filteredProducts.length > 0 ? 0 : null
            this.showDropdownMenu = this.filteredProducts.length > 0
        }, 100),

        filterProducts() {
            const searchResult = Product.search(this.search, this.products, 30, this.disableQuantity)

            if(searchResult.quantity) {
                this.quantity = searchResult.quantity
            }

            this.filteredProducts = searchResult.products

            if(searchResult.query || searchResult.query === '') {
                this.search = searchResult.query
            }
        },

        selectProduct(product, quantity = this.quantity) {
            if(this.product?.id === product?.id && this.quantity == quantity) {
                return;
            }

            this.$emit('select', { product: product, quantity: quantity });
            this.showDropdownMenu = false;

            if (this.preserveSelection) {
                this.product = product;
                this.search = product.name;
                this.$refs.searchField.blur()
            } else {
                this.resetSearch();
            }
        },

        setSelected(product) {
            this.product = product || null;
            this.search = product?.name || null;
        },

        onSearchBlur() {
            if(this.showDropdownMenu) return

            this.resetSearch()

            setTimeout(() => {
                this.$emit('blur');
            }, 200)
        },

        resetSearch() {
            if (this.preserveSelection) {
                this.search = this.product?.name || null
                return;
            };

            this.search = null
            this.quantity = null
        },

        clear() {
            if (this.preserveSelection) {
                this.product = null;
                this.$emit('select', { product: null, quantity: null });
            }
        },

        focus() {
            if (this.$refs.searchField && !this.$refs.searchField.isFocused) {
                this.$refs.searchField.focus()
            }
        },

        menuClickOutside() {
            this.showDropdownMenu = false;
            this.resetSearch();
        },

        async scanBarcode() {
            try {
                // Check camera permission
                await BarcodeScanner.checkPermission({ force: true });

                // make background of WebView transparent
                BarcodeScanner.hideBackground();
                const result = await BarcodeScanner.startScan();

                this.$refs.searchField.focus();

                if(!result.hasContent) return;

                this.search = result.content

                const pressEnterEvent = new KeyboardEvent('keyup', { bubbles: true, cancelable: true, keyCode: 13 });

                this.$nextTick(() => {
                    this.$refs.searchField.$refs.input.dispatchEvent(pressEnterEvent)
                })
            } catch(error) {
                console.log(error.message)
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.search-product {
    position: relative;
    background: white;
    display: flex;
    z-index: 1;

    .select-products-menu {
        position: absolute;
        left: 0;
        right: 0;
        max-height: 335px;
        overflow-y: auto;
        border-bottom: 1px solid #b0b2bc;
        border-top: 1px solid #b0b2bc;
        box-shadow: 0 4px 6px 0 rgb(32 33 36 / 28%);

        ::v-deep .v-list {
            .search-item {
                height: 67px;

                .price-pre-tax {
                    color: #676767;
                }

                &.active {
                    color: var(--v-primary-base) !important;
                    background: #edf0f5;

                    .v-list-item__subtitle {
                        color: var(--v-primary-base) !important;
                    }

                    .price-pre-tax {
                        color: var(--v-primary-base) !important;
                    }
                }
            }
        }
    }
}
</style>
